import React from 'react';
import { marked } from 'marked';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// --- Helpers ---
const removeAnnotations = (text) => {
  if (typeof text !== 'string') return String(text || '');
  return text.replace(/【[^】]*】/g, '');
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// --- Math placeholder extraction (from RAW text) ---
const extractMathToPlaceholders = (raw) => {
  let text = raw ?? '';
  const tokens = [];
  let idx = 0;

  const addToken = (latex, displayMode) => {
    const id = `@@MATH_${idx++}@@`;
    tokens.push({ id, latex: latex.trim(), displayMode: !!displayMode });
    return id;
  };

  // 1) Block math: $$ ... $$
  text = text.replace(/\$\$([\s\S]+?)\$\$/g, (_, body) => addToken(body, true));

  // 2) Block math: \[ ... \]
  text = text.replace(/\\\[([\s\S]+?)\\\]/g, (_, body) => addToken(body, true));

  // 3) Inline math: \( ... \)
  text = text.replace(/\\\(([\s\S]+?)\\\)/g, (_, body) => addToken(body, false));

  // 4) Inline math: $ ... $ (single-line)
  text = text.replace(/\$([^\n$]+?)\$/g, (_, body) => addToken(body, false));

  // 5) Specific Nur Tutor style inline fractions: (\frac{a}{b})
  text = text.replace(/\(\\frac\{(.+?)\}\{(.+?)\}\)/g, (_, num, den) =>
    addToken(`\\frac{${num}}{${den}}`, false)
  );

  return { textWithPlaceholders: text, tokens };
};

// --- Replace tokens with KaTeX HTML IN HTML string ---
const injectKatexIntoHtml = (html, tokens) => {
  let out = html;

  for (const { id, latex, displayMode } of tokens) {
    const rendered = (() => {
      try {
        return katex.renderToString(latex, {
          throwOnError: false,
          displayMode,
        });
      } catch (e) {
        console.error('KaTeX render error:', e, 'for:', latex);
        return latex; // fallback to raw latex
      }
    })();

    // Replace ALL occurrences of the token
    out = out.replace(new RegExp(escapeRegExp(id), 'g'), rendered);
  }

  // Final fallback: render any leftover \frac{...}{...} that slipped through
  out = out.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, (m, a, b) => {
    try {
      return katex.renderToString(`\\frac{${a.trim()}}{${b.trim()}}`, {
        throwOnError: false,
        displayMode: false,
      });
    } catch {
      return m;
    }
  });

  return out;
};

// --- Full pipeline ---
const processInput = (rawText) => {
  const cleaned = removeAnnotations(rawText ?? '');

  // Step 1: extract math → placeholders
  const { textWithPlaceholders, tokens } = extractMathToPlaceholders(cleaned);

  // Step 2: Markdown → HTML
  const htmlFromMarkdown = marked.parse(textWithPlaceholders);

  // Step 3: inject KaTeX HTML for tokens
  const htmlWithMath = injectKatexIntoHtml(htmlFromMarkdown, tokens);

  // Step 4: sanitize (allow style so KaTeX spacing is preserved)
  const safeHtml = DOMPurify.sanitize(htmlWithMath, { ADD_ATTR: ['style'] });

  // Step 5: HTML → React nodes
  return parse(safeHtml);
};

// --- Component ---
export const RenderMarkdown = ({ markdown }) => {
  const rendered = processInput(markdown);
  return <>{rendered}</>;
};
